import { EventBus, Events } from '@/lib/events'

import {
  buildPresetFromLogoState,
  setCurrentLogo,
  nav,
} from '../lib/helpers/editor'
import { ResourceNamesEnum } from '@/services/api/api-service'

import {
  PALETTE_PROPERTIES,
  ASSET_LIST_NAMES,
  createStoreDefaults,
  DEFAULT_SLOGAN_SCALE,
} from '../lib/constants/editor'

import {
  RESET,
  SET_LOGO,
  SET_SVG,
  SET_LOGO_PRESET_PROPERTY,
  SET_PALETTE_HEX,
  SET_PALETTE_PROPERTY,
  SET_TEMPLATE_PREVIEWS,
  ADD_TEMPLATE_PREVIEWS,
  SET_ASSET_LIST,
  ADD_PALETTE_COLOR,
  SET_PALETTE_COLORS,
  SET_ICON_SEARCH_STRING,
  SET_ICON_SEARCH_RESULTS,
  TOGGLE_LOADING_OVERLAY_VISIBILITY,
  SET_UNVALIDATED_SLOGAN,
  SET_UNVALIDATED_WORDMARK,
  SET_UNVALIDATED_KEYWORDS,
  SET_CUSTOM_KEYWORDS,
  SET_SLOGAN_SCALE,
  SET_ICON_SCALE,
  SET_ICON_VERTICAL_OFFSET,
  SET_ICON_HORIZONTAL_OFFSET,
  ADD_NOUN_PROJECT_RESULTS,
  SET_ICON_ORIENTATION,
  SET_SLOGAN_ALIGNMENT,
  SET_WORDMARK_ALIGNMENT,
  SET_SCOPE,
  SET_MODE,
  SET_FEATURES,
  SET_WORDMARK_MULTICOLOR,
  SET_WORDMARK_MULTILINE,
  SET_SLOGAN_MULTICOLOR,
  SET_SLOGAN_MULTILINE,
  SET_TEMPLATE_ALIGNMENT,
  SET_ICON_VISIBILITY,
  SET_FONT_CATEGORIES,
  SET_PALETTE_THEME,
  TOGGLE_PALETTE_THEME,
  SET_SHAPE,
  TOGGLE_FONT_CATEGORIES,
  SET_SELECTED_ICONS,
  TOGGLE_SELECTED_ICON,
  SET_FONT_TYPES,
  MOVE_FONT_TO_TOP_OF_LIST,
  INCR_UPDATE_KEY,
  INCREMENT_REFRESH_KEY,
  SET_TIGHT_BOUNDS,
  ADD_TO_PALETTES,
  SET_WORDMARK_SCRIPT,
  SET_MASK_STROKE,
  SET_GRADIENT_PALETTES,
  SET_MONOGRAM_TEXT,
  SET_HAS_MORE_PALETTES,
} from '@/lib/helpers/editor-mutations'
import { shuffleArray } from '../lib/helpers/array'
import { FONT_NOT_FOUND_ERROR } from '../util/error-types'
import { processLDELogoForSaving } from '../lib/helpers/logos'
import { useLocalStorage } from '@/lib/helpers/storage'

export const state = () => ({
  mode: 'create',

  // Let various editors access their own Stores
  scope: 'editor',

  template_previews: [],

  hasMorePalettes: true,
  // asset lists
  palettes: [],
  hues: [],
  templates: [],
  fonts: [],

  gradient_palettes: [],

  // used to store the response from /edit
  logo: {},

  // raw slogan and wordmark contain copies of the logo text
  // used for validation
  unvalidated_wordmark: '',
  unvalidated_slogan: '',
  unvalidated_keywords: '',
  custom_keywords: [],

  wordmark_script: false,
  slogan_script: false,

  // api response ONLY
  // TODO: Fill this out with all properties so that they can be referenced without null checks
  current_logo: null,

  logo_preset: { ...createStoreDefaults().logo_preset },

  // icon search results
  // todo move to component state
  icon_search_string: null,
  icon_search_results: null,
  noun_project_search_results: null,

  icon_scale: 1,
  icon_vertical_offset: 0,
  icon_horizontal_offset: 0,
  icon_orientation: 'default',

  slogan_alignment: null,
  wordmark_alignment: null,

  wordmark_multicolor: 'default',
  wordmark_multiline: 'default',
  slogan_multicolor: 'default',
  slogan_multiline: 'default',
  slogan_scale: DEFAULT_SLOGAN_SCALE,

  template_alignment: 'default',
  shape: 'none',

  icon_visibility: 'default',
  palette_theme: [],
  font_categories: [],
  font_styles: [],
  selected_icons: [],
  stroke_mask_width: 'default',

  refresh_key: 0,
  // vuex controlling editor view state
  view: {
    loading_overlay_visible: false,
  },

  features: [...createStoreDefaults().features],

  finishPrompt: 'Get your logo!',
  update_key: 0,
  tight_bounds: '',
  monogram_text: '',
})

export const getters = {
  assets_loaded: state =>
    state.fonts.length > 0 &&
    state.palettes.length > 0 &&
    state.templates.length > 0,
  loading_overlay_visible: state => state.view.loading_overlay_visible,

  activeControl: ({ scope }) => ({ path }) =>
    path
      .replace(/\/?editor\/?/, '')
      .split(scope)
      .pop()
      .replace('/', ''),

  finishPrompt({ scope, finishPrompt }, getters, rootState) {
    return rootState[scope].finishPrompt || finishPrompt
  },

  templateOptions({ current_logo }) {
    return current_logo?.assets?.template || {}
  },
}

export const mutations = {
  [SET_HAS_MORE_PALETTES](state, value) {
    state.hasMorePalettes = value
  },
  [SET_MONOGRAM_TEXT](state, value) {
    state.monogram_text = value
  },
  [SET_GRADIENT_PALETTES](state, value) {
    state.gradient_palettes = value
  },
  [ADD_TO_PALETTES](state, value) {
    if (!state.palettes) state.palettes = []
    state.palettes.push(...value)
  },

  [SET_TIGHT_BOUNDS](state, value) {
    state.tight_bounds = value
  },
  [INCR_UPDATE_KEY](state) {
    if (isNaN(state.update_key)) {
      state.update_key = 0
    } else {
      state.update_key += 1
    }
  },
  [SET_ICON_SCALE](state, scale) {
    state.icon_scale = parseFloat(scale)
  },
  [SET_ICON_ORIENTATION](state, orientation) {
    state.icon_orientation = orientation
  },
  [SET_ICON_VERTICAL_OFFSET](state, offset) {
    state.icon_vertical_offset = parseInt(offset)
  },
  [SET_ICON_HORIZONTAL_OFFSET](state, offset) {
    state.icon_horizontal_offset = parseInt(offset)
  },
  [SET_SLOGAN_SCALE](state, scale) {
    state.slogan_scale = parseFloat(scale)
  },
  [SET_SLOGAN_ALIGNMENT](state, align) {
    state.slogan_alignment = align
  },
  [SET_WORDMARK_ALIGNMENT](state, align) {
    state.wordmark_alignment = align
  },

  /**
   * Set unvalidated slogan
   * The value is never set to the API unless validated
   * @param {*} state
   * @param {*} slogan unvalidated user input
   */
  [SET_UNVALIDATED_SLOGAN](state, slogan) {
    state.unvalidated_slogan = slogan
  },

  [SET_MASK_STROKE](state, value) {
    state.stroke_mask_width = value
  },

  /**
   * Set unvalidated wordmark
   * @param {*} state
   * @param {*} wordmark unvalidated user input
   */
  [SET_UNVALIDATED_WORDMARK](state, wordmark) {
    state.unvalidated_wordmark = wordmark
  },

  /**
   * Set unvalidated keywords
   * @param {*} state
   * @param {*} keywords unvalidated user input
   */
  [SET_UNVALIDATED_KEYWORDS](state, keywords) {
    state.unvalidated_keywords = keywords
  },

  /**
   * Set custom keywords.
   *
   * @param {object} state
   * @param {array} keywords keywords iinput from the user
   */
  [SET_CUSTOM_KEYWORDS](state, keywords) {
    state.custom_keywords = keywords
  },

  /**
   * Toggles loading overlay
   * @param {*} state
   * @param {*} visibility if true, loading overlay is visible
   */
  [TOGGLE_LOADING_OVERLAY_VISIBILITY](state, visibility) {
    state.view.loading_overlay_visible = !!visibility
  },

  [SET_WORDMARK_SCRIPT](state, config) {
    state.wordmark_script = config
  },

  /**
   * Naively increments an integer used as the editor's preview key attribute.
   */
  [INCREMENT_REFRESH_KEY](state) {
    state.refresh_key++
  },

  /**
   * setter for icon search results.
   * NOTE: should be in component state
   * @param {*} state
   * @param {*} payload.type accepted values: noun_project logo_db
   * @param {*} payload.data icon data
   */
  [SET_ICON_SEARCH_RESULTS](state, payload) {
    if (payload.type === 'noun_project') {
      state.noun_project_search_results = payload.data
    }
    if (payload.type === 'logo_db') {
      state.icon_search_results = payload.data
    }
  },

  [ADD_NOUN_PROJECT_RESULTS](state, payload) {
    state.noun_project_search_results = state.noun_project_search_results.concat(
      payload
    )
  },

  /**
   * Sets string sent to the icon search
   * @param {string} input user input
   */
  [SET_ICON_SEARCH_STRING](state, input) {
    state.icon_search_string = input
  },

  /**
   * Sets one of the editor asset list
   * @param {*} state
   * @param {string} param.list which list shuold be set? use ResourceNamesEnum
   * @param {*} param.value asset data as json
   */
  [SET_ASSET_LIST](state, { list, value }) {
    state[list] = value
  },

  /**
   * Not all templates use all colors.
   * So, in the custom palette UI, we only let users interact with the colors
   * in their current logo
   * This is the setter for the array that controls which color swatches are
   * shown to the user
   * @param {} state
   * @param {Array} payload array of palette colors, IE ['background', 'primary'].
   *    possible values background primary accent secondary
   */
  [SET_PALETTE_COLORS](state, payload) {
    state.logo_preset.palette_colors = payload
  },

  /**
   * Add a color to the array that controls which color swatches are shown
   * on the editor
   * see SET_PALETTE_COLORS
   * @param {string} color accepted values accent background primary secondary
   */
  [ADD_PALETTE_COLOR](state, color) {
    if (state.logo_preset.palette_colors.includes(color)) return false
    state.logo_preset.palette_colors.push(color)
  },

  /**
   * sets the template previews array, which feeds the TemplatesControl
   * on /editor
   * in practice this is mostly used to clear the array
   * @param {Array} value templates list as array - response from logo-api
   */
  [SET_TEMPLATE_PREVIEWS](state, value) {
    state.template_previews = value
  },

  /**
   * concats an array to the template previews array
   * see also: SET_TEMPLATE_PREVIEWS
   * @param {Array} value templates list as array - response from logo-api
   */
  [ADD_TEMPLATE_PREVIEWS](state, value) {
    state.template_previews = state.template_previews.concat(value)
  },

  /**
   * Sets an individual property of the palette_hex object
   * Sets palette to custom so that the vuex state knows to use the palette hex
   * instead of the palette id
   * @param {string} param.property which property of palette_hex is being set? primary accent secondary background
   * @param {string} param.value hex value
   */
  [SET_PALETTE_PROPERTY](state, { property, value }) {
    state.logo_preset[ResourceNamesEnum.Palettes] = 'CUSTOM'
    state.logo_preset.palette_hex[property] = value
  },

  /**
   * replaces palette_hex object wholesale
   * only use this function in cases where your sure the whole object
   * is populated
   * @param {object} palette palette object
   */
  [SET_PALETTE_HEX](state, palette) {
    if (!state.logo_preset.palette_hex) {
      state.logo_preset.palette_hex = {}
    }
    PALETTE_PROPERTIES.forEach(prop => {
      if (palette[prop]) {
        state.logo_preset.palette_hex[prop] = palette[prop]
      }
    })
  },

  /**
   * replaces current_logo obeject, which is what is fed to /pricing
   * use logo-api response
   * @param {object} logo logo as object
   */
  [SET_LOGO](state, logo) {
    state.current_logo = logo
  },

  /**
   * sets an individual property of the logo preset
   * the logo preset is used to build the request which is sent to the logo-api
   * NOTE: the slogan and wordmark of the logo request are taken from session
   * @param payload.resource which property of the preset is being set?
   *  accepted values: ResourcesNamesEnum Templates Palettes Wordmark_fonts Slogan_fonts Icons
   *
   */
  [SET_LOGO_PRESET_PROPERTY](state, payload) {
    state.logo_preset[payload.resource] = payload.id
  },

  /**
   * set the svg property which powers the editor's preview
   * @param {string} svg logo as svg
   */
  [SET_SVG](state, svg) {
    state.logo_preset.svg = svg
  },

  [RESET](state) {
    state.logo_preset = createStoreDefaults().logo_preset
    state.unvalidated_wordmark = ''
    state.unvalidated_slogan = ''
    state.current_logo = null
    useLocalStorage().removeItem('logoId')
  },

  [SET_SCOPE](state, scope = 'editor') {
    state.scope = scope
  },

  [SET_MODE](state, mode = 'create') {
    state.mode = mode
  },

  [SET_FEATURES](state, features = null) {
    state.features = features
      ? [...features]
      : [...createStoreDefaults().features]
  },

  [SET_WORDMARK_MULTICOLOR](state, value) {
    state.wordmark_multicolor = value
  },
  [SET_WORDMARK_MULTILINE](state, value) {
    state.wordmark_multiline = value
  },
  [SET_SLOGAN_MULTICOLOR](state, value) {
    state.slogan_multicolor = value
  },
  [SET_SLOGAN_MULTILINE](state, value) {
    state.slogan_multiline = value
  },
  [SET_SHAPE](state, value) {
    state.shape = value
  },
  [SET_TEMPLATE_ALIGNMENT](state, value) {
    state.template_alignment = value
  },
  [SET_ICON_VISIBILITY](state, value) {
    state.icon_visibility = value
  },

  // Accepts an array of icons
  [SET_SELECTED_ICONS](state, value) {
    state.selected_icons = value
  },

  // Accepts an icon from the API
  [TOGGLE_SELECTED_ICON](state, value) {
    if (state.selected_icons.find(icon => icon.id === value.id)) {
      state.selected_icons = state.selected_icons.filter(
        item => item.id !== value.id
      )
    } else {
      state.selected_icons.push(value)
    }
  },

  [SET_FONT_CATEGORIES](state, value) {
    state.font_categories = [...value]
  },

  [SET_FONT_TYPES](state, value) {
    state.font_styles = value
  },

  [TOGGLE_FONT_CATEGORIES](state, value) {
    // Make sure we're dealing with an Array
    if (typeof state.font_categories === 'string') {
      state.font_categories = []
    }

    if (state.font_categories.includes(value)) {
      state.font_categories = state.font_categories.filter(
        item => item !== value
      )
    } else {
      state.font_categories.push(value)
    }
  },

  [SET_PALETTE_THEME](state, value) {
    state.palette_theme = value
  },

  [TOGGLE_PALETTE_THEME](state, value) {
    // Make sure we're dealing with an Array
    if (typeof state.palette_theme === 'string') {
      state.palette_theme = []
    }

    if (state.palette_theme.includes(value)) {
      state.palette_theme = state.palette_theme.filter(item => item !== value)
    } else {
      state.palette_theme.push(value)

      EventBus.$emit(Events.EcommerceSelectContent, {
        type: 'palette_category',
        id: value,
      })
    }
  },
  /**
   * re-order fonts so that font with provided id is the first one
   * @param {*} value.id id of font to move to top of list
   */
  [MOVE_FONT_TO_TOP_OF_LIST](state, value) {
    const { id } = value
    const font = state.fonts.find(font => font.id === id)
    if (!font) throw new Error(FONT_NOT_FOUND_ERROR)
    const idx = state.fonts.indexOf(font)
    state.fonts.splice(idx, 1)
    state.fonts.unshift(font)
  },
}

export const actions = {
  async addClientLogoToState({ dispatch, rootState }, { logo, params }) {
    const { wordmark, slogan } = rootState.session
    const logo_dto = processLDELogoForSaving(logo, wordmark, slogan)
    await dispatch('setState', {
      logo: logo_dto,
      params,
    })
  },

  async fetchGradientPalettes({ commit }) {
    const path = `/api/v3/assets/palettes/gradients`
    const response = await this.$api.get(path, { quantity: 20 })
    commit('SET_GRADIENT_PALETTES', response.data)
  },

  async fetchPaletteList({ commit, state }) {
    if (state.palettes.length > 0 && !state.hasMorePalettes) {
      return
    }
    const params = {
      quantity: 100,
      offset: state.palettes.length,
      full_response: true,
      order: 'asc',
    }
    const path = `/api/v3/assets/palettes`

    try {
      const response = await this.$api.get(path, { params })
      const { data, hasMore } = response.data
      shuffleArray(data)
      commit(SET_ASSET_LIST, {
        list: 'palettes',
        value: data,
      })
      commit(SET_HAS_MORE_PALETTES, hasMore)
      return response
    } catch (error) {
      this.$sentry.captureException(error)
    }
  },
  /**
   * Fetches and stores in state a single asset list.
   * if fonts, adds the font files to dom from our CMS
   * @param {*} resource use ASSET_LIST_NAMES
   */
  async fetchList({ commit }, resource) {
    const params = {
      quantity: 200,
    }
    const path = `/api/v3/assets/${resource}`

    if (resource === 'templates') {
      params.shuffle = true
    }

    try {
      const response = await this.$api.get(path, { params })
      const result = response.data
      if (resource === 'fonts') {
        for (let i = 0; i < result.length; i++) {
          result[i].url = `https://cdn.logo.com/font/${result[i].id}`
        }
      }
      shuffleArray(result)
      commit(SET_ASSET_LIST, {
        list: resource,
        value: result,
      })
    } catch (error) {
      this.$sentry.captureException(error)
    }
  },
  async fetchTemplates({ commit }, args) {
    try {
      const params = { quantity: 200 }
      if (args) {
        const { tag } = args
        if (tag) {
          if (tag === 'all') {
            params.tags = []
          } else {
            params.tags = [tag]
          }
        }
      }
      const { data } = await this.$api.get('/api/v3/assets/layouts', {
        params: { ...params },
      })
      shuffleArray(data)
      if (args && args.commit === true) {
        commit(SET_ASSET_LIST, { list: 'templates', value: data })
      }
      return data
    } catch (error) {
      this.$sentry.captureException(error)
    }
  },

  /**
   * Fetches and stores a list of Font Families.
   */
  async fetchFonts({ commit }, args) {
    const params = {
      return_families: true,
      tags: ['Suggested Fonts'],
      return_all_assets: true,
      quantity: 2000,
      u: 0,
    }
    if (args) {
      const { tag, type, name_search } = args
      if (tag) {
        if (tag === 'all') {
          params.tags = []
        } else {
          params.tags = [tag]
        }
      }
      if (name_search && name_search.length > 0) {
        params.name_search = name_search
      }
      if (type) {
        if (type === 'all') {
          params.styles = []
        } else {
          params.styles = [type]
        }
      }
    }
    try {
      const { data } = await this.$api.get('/api/v3/assets/typefaces', {
        params,
      })
      shuffleArray(data)
      commit(SET_ASSET_LIST, { list: 'fonts', value: data })
    } catch (error) {
      this.$sentry.captureException(error)
    }
  },

  /**
   * Iterates over ASSET_LIST_NAMES to fetch all the asset lists that
   * the editor needs
   */
  async fetchAssetLists({ dispatch, state, commit, rootState }) {
    ASSET_LIST_NAMES.forEach(resource => {
      commit(SET_ASSET_LIST, {
        list: resource,
        value: [],
      })
    })

    const to_fetch = []

    if (!state.templates || state.templates.length === 0) {
      to_fetch.push(dispatch('fetchTemplates', { commit: true }))
    }

    if (!state.fonts || state.fonts.length === 0) {
      to_fetch.push(dispatch('fetchFonts'))
    }

    if (!state.palettes || state.palettes.length === 0) {
      to_fetch.push(dispatch('fetchPaletteList'))
    }
    if (rootState.flags.ideas_gradients) {
      to_fetch.push(dispatch('fetchGradientPalettes'))
    }

    return Promise.all(to_fetch)
  },

  createLogoPreset({ state, rootState }, params) {
    let disable_preference_mode = true
    let design_alignment
    let disable_text_casing = true

    if ('disable_text_casing' in params) {
      disable_text_casing = params.disable_text_casing
    }

    if ('disable_preference_mode' in params) {
      disable_preference_mode = params.disable_preference_mode
    }

    if ('design_alignment' in params) {
      design_alignment = params.design_alignment
    }

    return buildPresetFromLogoState(
      state,
      disable_text_casing,
      disable_preference_mode,
      rootState.session.wordmark,
      rootState.session.slogan,
      state.icon_scale,
      state.icon_horizontal_offset,
      state.icon_vertical_offset,
      state.slogan_alignment,
      state.icon_orientation,
      state.wordmark_alignment,
      design_alignment,
      state.slogan_scale
    )
  },

  /**
   * This function uses state.logo_preset to build an api request
   * it then saves the raw api response to current_logo
   * and populates logo_preset with the required data
   * @param {boolean} params.disable_text_casing - some templates have text formatting features. this flag disables them
   * @param {boolean} params.disable_preference_mode - some templates have internal logic that decides whether or not to show an icon or slogan. setting this to true will override that
   */

  async fetchNewLogoFromState({ commit, dispatch, rootState }, params = {}) {
    if (rootState.flags.client_side_logos) {
      EventBus.$emit(Events.EditorRefreshLogo)

      return
    }
    commit(INCR_UPDATE_KEY)
    const preset = dispatch('createLogoPreset', params)

    if (!preset.wordmark) {
      return
    }

    preset.quantity = 1

    if (!params.hide_loader) {
      commit(TOGGLE_LOADING_OVERLAY_VISIBILITY, true)
    }

    preset.eid = rootState.enterprise.id

    if (!rootState.flags.client_side_logos) {
      try {
        const { data } = await this.$api.post('/api/v2/logos', preset)
        setCurrentLogo({ commit, dispatch }, { logo: data[0], params })
      } catch (error) {
        this.$sentry.captureException(error)
      } finally {
        if (!params.hide_loader) {
          commit(TOGGLE_LOADING_OVERLAY_VISIBILITY, false)
        }
      }
    }
    if (!params.hide_loader) {
      commit(TOGGLE_LOADING_OVERLAY_VISIBILITY, false)
    }
  },

  /**
   * Populates logo_preset state with logo api response
   * @param {*} logo object response from logo-api
   */
  setState({ commit, dispatch }, { logo, params = {} }) {
    setCurrentLogo({ commit, dispatch }, { logo, params })
  },

  /**
   * Populates logo_preset ID and persists it to useLocalStorage()
   * @param {String} id the ID of a logo
   */
  setLogoId({ commit }, id) {
    commit(SET_LOGO_PRESET_PROPERTY, { id, resource: 'id' })

    // 1 week
    const expires = Date.now() + 7 * 24 * 60 * 60 * 1000

    useLocalStorage().setItem('logoId', JSON.stringify({ id, expires }))
  },

  resetIconOffset({ commit }) {
    commit(SET_ICON_VERTICAL_OFFSET, 0)
    commit(SET_ICON_HORIZONTAL_OFFSET, 0)
  },

  resetIconPosition({ commit, dispatch }) {
    dispatch('resetIconOffset')
    commit(SET_ICON_SCALE, 1)
  },

  /**
   * Helper to create a blank template with a black/white palette
   * and generic Sans Serif font.
   */
  async createBlankLogo({ rootState, dispatch, commit }) {
    // Template Zero 🧟‍♂️
    const templateId = 'c19a83b5-17cb-42df-9a01-72d89fbb6624'

    // Open Sans Bold 💬
    const wordmarkFont = '298bc5fe-b865-4204-bdaf-d3c6182d3447'

    // Source Sans Pro Light 💬
    const sloganFont = '4c112675-c936-4610-a738-1c05b3e1106b'

    // Black/White Palette 🎨
    const palette = {
      id: '44a6435a-331a-4ddf-ad61-72afa48cf8c3',
      hex: {
        background: '#FFFFFF',
        quaternary: '#FFFFFF',
        secondary: '#262626',
        tertiary: '#262626',
        primary: '#262626',
        accent: '#262626',
      },
    }

    const { wordmark } = rootState.session

    // Resources 👨🏻‍💻
    const {
      Wordmark_fonts,
      Slogan_fonts,
      Templates,
      Palettes,
      Icons,
    } = ResourceNamesEnum

    // Build our generic template preset 🏗
    commit(SET_LOGO_PRESET_PROPERTY, { id: templateId, resource: Templates })
    commit(SET_LOGO_PRESET_PROPERTY, { id: palette.id, resource: Palettes })
    commit(SET_LOGO_PRESET_PROPERTY, {
      id: wordmarkFont,
      resource: Wordmark_fonts,
    })
    commit(SET_LOGO_PRESET_PROPERTY, { id: sloganFont, resource: Slogan_fonts })
    commit(SET_LOGO_PRESET_PROPERTY, { id: '', resource: Icons })
    commit(SET_WORDMARK_ALIGNMENT, 'left')
    commit(SET_PALETTE_HEX, palette.hex)
    commit(SET_SLOGAN_ALIGNMENT, 'left')
    commit(SET_SHAPE, 'remove')
    commit(SET_WORDMARK_ALIGNMENT, 'center')
    commit(SET_PALETTE_HEX, palette.hex)
    commit(SET_SLOGAN_ALIGNMENT, 'center')

    commit('session/setWordmark', wordmark.toUpperCase(), { root: true })

    // Fetch the logo 🚀
    await dispatch('fetchNewLogoFromState')
  },

  init({ dispatch }) {
    if (process.static) return

    try {
      // Grab the logo from storage - if it doesn't exist it'll throw into
      // the catch
      const logo = JSON.parse(useLocalStorage().getItem('logoId'))

      if (!logo && !logo.id) {
        return
      }

      // If we have a logo, and it's not expired, give it to the store and
      // refresh useLocalStorage()
      if (logo.expires > Date.now()) {
        dispatch('setLogoId', logo.id)
      } else {
        useLocalStorage().removeItem('logoId')
      }
    } catch (error) {
      useLocalStorage().removeItem('logoId')
    }
  },

  /**
   * Set a custom palette property, and if needed, override some optional
   * properties.
   *
   * @param {{property: string, value: string}} palette The palette property and value to update.
   */
  setPaletteProperty({ dispatch, commit, state }, palette) {
    const { property, value } = palette

    const stateVal = state.logo_preset.palette_hex[property]
    const paletteVal = Array.isArray(stateVal) ? [value, stateVal[1]] : value

    commit(SET_PALETTE_PROPERTY, {
      property,
      value: paletteVal,
    })

    if (palette.property === 'primary' || palette.property === 'background') {
      dispatch('setOptionalPaletteProperties', palette)
    }
  },

  setPaletteSecondaryProperty({ commit, state }, palette) {
    const { property, value } = palette

    const stateVal = state.logo_preset.palette_hex[property]

    const paletteVal = Array.isArray(stateVal) ? stateVal[0] : stateVal
    commit(SET_PALETTE_PROPERTY, {
      property,
      value: [paletteVal, value],
    })
  },

  /**
   * Override tertiary/quaternary colors if the template doesn't use them.
   * This is needed because we hide the colour inputs if they have no action
   * on the current template, but we still want to update those properties so
   * /designs doesn't have mis-matched palettes.
   *
   * @param {{property: string, value: string}} palette The palette property and value to update.
   */
  setOptionalPaletteProperties({ commit, state }, { property, value }) {
    const { colors_used } = state.current_logo?.assets?.template || {}

    // If we're updating the primary color and the template doesn't use tertiary, set tertiary to primary
    if (property === 'primary' && !colors_used.includes('tertiary')) {
      commit(SET_PALETTE_PROPERTY, { property: 'tertiary', value })
    }

    // If we're updating the background color and the template doesn't use quaternary, set quaternary to background
    if (property === 'background' && !colors_used.includes('quaternary')) {
      commit(SET_PALETTE_PROPERTY, { property: 'quaternary', value })
    }
  },

  /*
   * Build our EditorNav Array based on a current set of Features either
   * from our Store Defaults, or from a Package.
   */
  async nav({ state, rootState }) {
    // Map the Route Slugs to our Feautres
    const map = {
      ideas: ['create'],
      templates: ['template'],
      icons: ['icons'],
      colors: ['palette'],
      containers: ['containers'],
      'business-name': ['fonts', 'wordmark'],
      slogan: ['fonts', 'slogan'],
      preview: ['preview'],
      history: ['history'],
    }

    // Wait for the locale aware nav to import!
    const items = await nav()

    return items.filter(({ slug, flag }) => {
      // Enable this control if we don't have a flag, or if the flag is enabled
      const enabled = !flag || rootState.flags[flag]

      return enabled && map[slug].some(feat => state.features.includes(feat))
    })
  },
}
